import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LabelV2Release,
  Release,
  ReleaseType,
  BaseVersion,
  Version,
} from 'types/releases';
import { ReleaseTitleForm } from 'app/components/Releases/ReleaseTitleForm';
import { ContentContainerType } from 'types/enums';
import { Box, Flex, Text, VStack, useMediaQuery } from '@chakra-ui/react';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { useAppSelector } from 'app/store';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import {
  getAudioPlayer,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { AudioActionButton } from 'app/components/Song/Audio/AudioActionButton';
import { breakpoints } from 'app/chakra-theme/foundations/breakpoints';
import { trimText } from 'app/utils/utils';
import { Route, routes } from 'app/routing/routesConfig';
import { DataTestId } from 'types/general.types';
import { ChakraBreadcrumbs } from 'app/components/Core';
import { ReleaseCover } from '../ReleaseCover';

export interface ReleaseHeaderProps {
  release: Release;
  version?: Version;
  songTitle?: string;
  children?: React.ReactNode;
}

export const ReleaseHeader: React.FC<ReleaseHeaderProps> = ({
  release,
  version,
  children,
  songTitle,
}) => {
  const showBreadcrumbs = release.type === ReleaseType.LABEL;

  const [breakpointMd] = useMediaQuery(`(max-width: ${breakpoints.md})`);
  const [breakpointLg] = useMediaQuery(`(max-width: ${breakpoints.lg})`);

  const getSongVersionTitle = (breadCrumb?: boolean) => {
    const type = version?.type || '';
    if (!songTitle) {
      return type;
    }

    const trimSize = breakpointMd ? 35 : breakpointLg ? 50 : 75;
    const songTitleTrimmed = trimText(
      songTitle,
      breadCrumb ? trimSize - 20 : trimSize
    );

    return `${songTitleTrimmed} - ${type}`;
  };

  const { loadAudio, playPauseAudio } = useAudioPlayer();
  const currentAudio = useAppSelector(getPlayerCurrentAudio);
  const { isPlaying } = useAppSelector(getAudioPlayer);

  const versions = useMemo(() => (release as LabelV2Release).versions, [
    release,
  ]);

  const [playingVersion, setPlayingVersion] = useState<BaseVersion>(
    versions?.[0]
  );

  useEffect(() => {
    if (!currentAudio) {
      return;
    }

    const version = versions?.find(({ primaryAudioGuid, originalAudioGuid }) =>
      [primaryAudioGuid, originalAudioGuid].includes(currentAudio.audioGuid)
    );
    if (version) {
      setPlayingVersion(version);
    }
  }, [currentAudio, versions]);

  const audioGuid = useMemo(
    () => playingVersion?.primaryAudioGuid || playingVersion?.originalAudioGuid,
    [playingVersion?.originalAudioGuid, playingVersion?.primaryAudioGuid]
  );

  const isLoading = useMemo(
    () => currentAudio?.audioGuid === audioGuid && currentAudio?.loading,
    [audioGuid, currentAudio?.audioGuid, currentAudio?.loading]
  );

  const handlePlay = async () => {
    if (!playingVersion) {
      return;
    }

    const parentGuid = playingVersion?.guid;
    const songTitle = playingVersion?.song.title;

    if (!audioGuid) {
      return;
    }

    if (currentAudio?.audioGuid === audioGuid) {
      playPauseAudio();
    } else {
      loadAudio({
        audio: [
          {
            audioGuid,
            parentGuid,
            songTitle,
          },
        ],
      });
    }
  };

  const getReleaseBreadcrumb = useCallback(
    (releaseType: ReleaseType): Array<Route & DataTestId> => {
      return [
        { 'data-testid': 'release-list', ...routes.releases },
        {
          ...routes.release,
          'data-testid': 'release-version-list',
          path: routes.release.path.replace(':guid', release.guid),
          name: trimText(release.title, 30),
        },
        ...(releaseType === ReleaseType.LABEL && version
          ? [
              {
                ...routes.releaseVersion,
                'data-testid': 'release-version',
                path: routes.releaseVersion.path
                  .replace(':guid', release.guid)
                  .replace(':versionGuid', version.guid),
                name: getSongVersionTitle(true),
              },
            ]
          : []),
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [release, version]
  );

  return (
    <Box bg="M.30">
      <ContentContainer type={ContentContainerType.LIST} py={6} flex="initial">
        <Flex>
          <ReleaseCover size="big" type={release.type} />
          <VStack pl={4} alignItems="baseline">
            {showBreadcrumbs ? (
              <ChakraBreadcrumbs routes={getReleaseBreadcrumb(release.type)} />
            ) : (
              <Text opacity={0.72}>Release</Text>
            )}
            <ReleaseTitleForm title={release.title} />
            <Flex gap={4} alignItems="center">
              {versions && (
                <AudioActionButton
                  playing={isPlaying && currentAudio.audioGuid === audioGuid}
                  onClick={handlePlay}
                  data-testid="audio-play-pause-button"
                  isLoading={isLoading}
                  size={32}
                  isPlayerTheme
                  playIconTestId="audio-play-icon"
                  pauseIconTestId="audio-pause-icon"
                  thickness={2}
                  isDisabled={versions.length === 0}
                />
              )}
              {children}
            </Flex>
          </VStack>
        </Flex>
      </ContentContainer>
    </Box>
  );
};
